import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Form,
  TextArea,
  Dimmer,
  Loader,
} from "semantic-ui-react";
import { DropDownList } from "../../common";

function getLeaveInfoMessage({ StartDate, EndDate, StartingLeave, EndingLeave }) {
  if (!StartDate || !EndDate) return null;

  // Calculate number of days
  const startDate = new Date(StartDate);
  const endDate = new Date(EndDate);

  // Check if end date is before start date (invalid range)
  if (endDate < startDate) {
    return "⚠️ Invalid date range: End date cannot be before start date";
  }

  const timeDiff = endDate.getTime() - startDate.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates

  const isSingleDay = new Date(StartDate).toDateString() === new Date(EndDate).toDateString();

  if (isSingleDay) {
    if (StartingLeave === 0 && EndingLeave === 0) return `🕑 First half only (Morning to LunchTime) - 1 day`;
    if (StartingLeave === 1 && EndingLeave === 1) return `🕑 Second half only (Afternoon to End of Day) - 1 day`;
    if (StartingLeave === 0 && EndingLeave === 1) return `🕑 Full day (Morning to End of Day) - 1 day`;
    return `🕑 Custom period - 1 day`;
  } else {
    let firstDay =
      StartingLeave === 0
        ? "Full day (Morning to End of Day)"
        : StartingLeave === 1
          ? "Second half only (Afternoon to End of Day)"
          : "Custom";
    let lastDay =
      EndingLeave === 0
        ? "First half only (Morning to LunchTime)"
        : EndingLeave === 1
          ? "Full day (Morning to End of Day)"
          : "Custom";
    return `📅 Multi-Day (${daysDiff} days): First day: ${firstDay}, Last day: ${lastDay}, Middle days: Full day`;
  }
}

const BulkGroupLeaveModal = ({
  t,
  IsModalOpen,
  handleCloseModal,
  onChange,
  handleDateChange,
  onSave,
  bulkLeaveModal,
  updateDropDown,
  isFormLoading,
  leaveTypeOptions,
  departmentOptions,
  startingLeaveOptions,
  endingLeaveOptions,
  isLoadingLeaveTypes = false,
  isLoadingDepartments = false,
}) => {
  const T = t || ((key) => key);
  const infoMessage = getLeaveInfoMessage(bulkLeaveModal);

  // Check if button should be disabled
  const hasLeaveTypeSelected = bulkLeaveModal.LeaveTypeId && bulkLeaveModal.LeaveTypeId !== "";
  const hasDepartmentSelected = bulkLeaveModal.DepartmentId && bulkLeaveModal.DepartmentId !== "";

  // Check for invalid date range
  const hasInvalidDateRange = bulkLeaveModal.StartDate && bulkLeaveModal.EndDate &&
    new Date(bulkLeaveModal.EndDate) < new Date(bulkLeaveModal.StartDate);

  const isButtonDisabled = !hasLeaveTypeSelected || !hasDepartmentSelected || hasInvalidDateRange;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .ui.dropdown .menu .item {
            color: #000000 !important;
          }
          .ui.dropdown .menu .item:hover {
            background: rgba(102, 126, 234, 0.1) !important;
            color: #000000 !important;
          }
          .ui.dropdown .menu .item.active {
            background: rgba(102, 126, 234, 0.1) !important;
            color: #000000 !important;
          }
          .ui.dropdown .menu .item.selected {
            background: rgba(102, 126, 234, 0.15) !important;
            color: #000000 !important;
            font-weight: 600 !important;
          }
        `}
      </style>
      {IsModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: 'blur(2px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
            }}
          >
            <Dimmer.Dimmable dimmed={isFormLoading}>
              <Dimmer active={isFormLoading} inverted={true}>
                <Loader>Loading</Loader>
              </Dimmer>
              <div className="modalInner">
                {/* Header Section */}
                <div className="modal-header-brand" style={{ position: "relative" }}>
                  <button
                    onClick={() => handleCloseModal()}
                    style={{
                      position: "absolute",
                      top: "20px",
                      right: "20px",
                      background: "rgba(255, 255, 255, 0.2)",
                      border: "none",
                      color: "white",
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "20px",
                      fontWeight: "bold",
                      transition: "all 0.3s ease",
                      lineHeight: "1",
                      padding: "0",
                      margin: "0",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    ×
                  </button>
                  <div className="d-flex align-items-center">
                    <div
                      className="me-3"
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="mb-1 fw-bold" style={{ fontSize: "1.5rem" }}>
                        Bulk Group Leave
                      </h3>
                      <p className="mb-0 text-white-50" style={{ fontSize: "0.9rem" }}>
                        Add leaves for all employees in a department
                      </p>
                    </div>
                  </div>
                </div>
                {/* Form Content */}
                <div style={{ padding: "24px" }}>
                  <Form>
                    {/* Department and Leave Type */}
                    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 20, gap: 16 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
                          Department
                        </label>
                        <DropDownList
                          selection
                          fluid
                          value={bulkLeaveModal.DepartmentId || ""}
                          options={departmentOptions || []}
                          name="DepartmentId"
                          onChange={updateDropDown}
                          style={{
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                          }}
                          placeholder={isLoadingDepartments ? T("app.modals.addLeave.loadingDepartments") : T("app.modals.addLeave.selectDepartment")}
                          disabled={isLoadingDepartments}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
                          Leave Type
                        </label>
                        <DropDownList
                          selection
                          fluid
                          value={bulkLeaveModal.LeaveTypeId || ""}
                          options={leaveTypeOptions || []}
                          name="LeaveTypeId"
                          onChange={updateDropDown}
                          style={{
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                          }}
                          placeholder={isLoadingLeaveTypes ? T("app.modals.addLeave.loadingLeaveTypes") : T("app.modals.addLeave.selectLeaveType")}
                          disabled={isLoadingLeaveTypes}
                        />
                      </div>
                    </div>

                    {/* Compact Info Message */}
                    {infoMessage && (
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#0c5460",
                          marginBottom: "12px",
                          padding: "8px 12px",
                          backgroundColor: "#e3f7fa",
                          borderRadius: "6px",
                          border: "1px solid #b8e2ea",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span style={{ fontWeight: "500" }}>{infoMessage}</span>
                      </div>
                    )}

                    {/* Dates and Periods in two columns */}
                    <div style={{ display: "flex", gap: 16 }}>
                      {/* From Date & Morning Period */}
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
                          From Date
                        </label>
                        <DatePicker
                          name="StartDate"
                          selected={bulkLeaveModal.StartDate ? new Date(bulkLeaveModal.StartDate) : null}
                          peekNextMonth
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          placeholderText="Select start date"
                          dateFormat="MM/dd/yyyy"
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "border-color 0.3s ease",
                          }}
                          onChange={(date) => handleDateChange("StartDate", date)}
                        />
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, marginTop: 12, display: "block" }}>
                          Morning Period
                        </label>
                        <DropDownList
                          selection
                          defaultValue={0}
                          options={startingLeaveOptions}
                          fluid
                          name="StartingLeave"
                          value={bulkLeaveModal.StartingLeave}
                          onChange={updateDropDown}
                          style={{
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                          }}
                        />
                      </div>
                      {/* To Date & Evening Period */}
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
                          To Date
                        </label>
                        <DatePicker
                          name="EndDate"
                          selected={bulkLeaveModal.EndDate ? new Date(bulkLeaveModal.EndDate) : null}
                          peekNextMonth
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          placeholderText="Select end date"
                          dateFormat="MM/dd/yyyy"
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "border-color 0.3s ease",
                          }}
                          onChange={(date) => handleDateChange("EndDate", date)}
                        />
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, marginTop: 12, display: "block" }}>
                          Evening Period
                        </label>
                        <DropDownList
                          selection
                          defaultValue={0}
                          options={endingLeaveOptions}
                          fluid
                          name="EndingLeave"
                          value={bulkLeaveModal.EndingLeave}
                          onChange={updateDropDown}
                          style={{
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                          }}
                        />
                      </div>
                    </div>

                    {/* Comments */}
                    <Form.Field>
                      <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: "8px", marginTop: "12px", display: "block" }}>
                        Comments
                      </label>
                      <TextArea
                        name="LeaveNote"
                        placeholder={T("app.modals.addLeave.commentsPlaceholder")}
                        onChange={onChange}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "1px solid #e9ecef",
                          borderRadius: "8px",
                          fontSize: "14px",
                          minHeight: "100px",
                          resize: "vertical",
                          transition: "border-color 0.3s ease",
                        }}
                      />
                    </Form.Field>

                    {/* Action Buttons */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "24px" }}>
                      <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                        <button
                          onClick={handleCloseModal}
                          style={{ padding: "12px 24px", border: "1px solid #6c757d", borderRadius: "8px", background: "white", color: "#6c757d", fontSize: "14px", fontWeight: "600", cursor: "pointer", transition: "all 0.3s ease" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f8f9fa";
                            e.currentTarget.style.borderColor = "#5a6268";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "white";
                            e.currentTarget.style.borderColor = "#6c757d";
                          }}
                        >
                          Close
                        </button>
                        <button
                          onClick={onSave}
                          className="btn-brand"
                          disabled={isButtonDisabled}
                          style={{
                            opacity: isButtonDisabled ? 0.6 : 1,
                            cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                            ...(isButtonDisabled && {
                              background: '#6c757d',
                              borderColor: '#6c757d'
                            })
                          }}
                        >
                          Add Bulk Leaves
                        </button>
                      </div>
                    </div>
                  </Form>
                </div>
              </div>
            </Dimmer.Dimmable>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkGroupLeaveModal;
